import { useEffect, useRef, useState } from "react";
import { playSound } from "../utils/gameAudio";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:3001";

/**
 * Which Prize Palette cue belongs to the public screen.
 * Spin start and the winner clap stay on the Spin the Wheel pack.
 */
export function prizePaletteSoundCue(screen) {
  if (!screen || typeof screen !== "object") return null;
  const kind = String(screen.kind || "");

  if (kind === "collage") {
    return { action: "collage_show", key: `collage:${screen.prize_id || screen.token || ""}` };
  }
  if (kind === "wheel") {
    const id = screen.wheel_session_id || screen.prize_id || "";
    return { action: "spin_wheel_show", key: `wheel:${id}` };
  }
  if (kind === "display") {
    return { action: "prize_show", key: `prize:${screen.prize_id || screen.token || ""}` };
  }
  if (kind === "reveal" || screen.status === "shown" || screen.status === "awarded") {
    const id = screen.award_id || screen.token || "";
    if (screen.status === "awarded") return { action: "prize_award", key: `award:${id}` };
    return { action: "prize_show", key: `show:${id}` };
  }
  return null;
}

const playConfigured = (map, action) => {
  const cfg = map?.[action];
  if (!cfg?.audio_url) return false;
  return Boolean(playSound(cfg.audio_url, { volume: cfg.volume ?? 1, loop: false }));
};

/**
 * Play a Prize Palette show/award cue once per visible screen change.
 * The opening snapshot is silent only when that screen is already audible,
 * so a refresh does not replay a prize that was already up.
 * A cue that arrives before the popup can be heard (spectator game still
 * loading, or the player is on another page) stays unplayed and fires
 * when that screen becomes visible.
 */
export default function usePrizePaletteEventAudio(screen, { audible = false, ready = false } = {}) {
  const soundsRef = useRef({});
  const primedRef = useRef(false);
  const lastKeyRef = useRef("");
  const pendingActionRef = useRef(null);
  const [soundsReady, setSoundsReady] = useState(false);

  const cue = prizePaletteSoundCue(screen);
  const cueKey = cue?.key || "";
  const action = cue?.action || "";

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/api/game-sounds/prize_palette`)
      .then((r) => r.json())
      .then((data) => {
        if (cancelled || !data?.success) return;
        const map = {};
        (data.sounds || []).forEach((sound) => {
          if (sound.enabled && sound.audio_url) map[sound.action_key] = sound;
        });
        soundsRef.current = map;
        setSoundsReady(true);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!ready) return;
    if (!primedRef.current) {
      primedRef.current = true;
      lastKeyRef.current = audible ? cueKey : "";
      pendingActionRef.current = null;
      return;
    }
    // Not on a screen that should hear this popup yet. Keep the cue unplayed.
    if (!audible) return;
    if (!cueKey) {
      lastKeyRef.current = "";
      pendingActionRef.current = null;
      return;
    }
    if (cueKey !== lastKeyRef.current) {
      lastKeyRef.current = cueKey;
      pendingActionRef.current = action || null;
    }
    const pending = pendingActionRef.current;
    if (!pending || !soundsReady) return;
    playConfigured(soundsRef.current, pending);
    pendingActionRef.current = null;
  }, [ready, audible, cueKey, action, soundsReady]);
}
